import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { PendingEmailAction } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { classNames } from "@/utils/classNames";
import { useSnackBar } from "@/utils/useSnackbar";

import { ProspectPendingEmail } from "@/pages/Private/pages/Apollo/schema/get-people";
import { useUpdateProspectPendingEmailMutation } from "@/pages/Private/redux/list/list.api";

interface PendingEmailBannerProps {
	listPersonId?: number;
	pendingEmails?: ProspectPendingEmail[] | null;
	className?: string;
}

/**
 * Shows the email candidates found for a prospect during outreach that have not been confirmed,
 * each with actions to confirm it (adopt as the prospect's real email) or reject it. Renders
 * nothing when there are no candidates. Reused by the prospect detail panel and the inbox view.
 */
export const PendingEmailBanner: FunctionComponent<PendingEmailBannerProps> = ({
	listPersonId,
	pendingEmails,
	className,
}) => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [updatePendingEmail, { isLoading }] = useUpdateProspectPendingEmailMutation();

	const actionableEmails = (pendingEmails ?? []).filter((e) => e.source !== "PREVIOUS_PRIMARY");

	if (!listPersonId || !actionableEmails.length) {
		return null;
	}

	const handleAction = async (
		pendingEmailId: number,
		action: PendingEmailAction
	): Promise<void> => {
		try {
			await updatePendingEmail({ id: listPersonId, pendingEmailId, action }).unwrap();
			handleOpenSnackbar(
				action === "CONFIRM" ? t("list.pendingEmail.confirmed") : t("list.pendingEmail.rejected")
			);
		} catch {
			handleOpenSnackbar(t("list.pendingEmail.error"));
		}
	};

	return (
		<div
			className={classNames(
				"flex flex-col gap-3 rounded-lg border border-status-warning bg-status-warning-light p-3",
				className || ""
			)}
		>
			<div className="flex flex-col gap-0.5">
				<span className="text-sm font-semibold">{t("list.pendingEmail.title")}</span>
				<span className="text-xs">{t("list.pendingEmail.description")}</span>
			</div>

			<div className="flex flex-col gap-2">
				{actionableEmails.map((pendingEmail) => (
					<div key={pendingEmail.id} className="flex flex-row items-center justify-between gap-2">
						<span className="text-sm font-semibold break-all">{pendingEmail.email}</span>

						<div className="flex shrink-0 flex-row gap-2">
							<div className="w-[130px]">
								<Button
									color={ButtonColor.SUCCESS}
									isLoading={isLoading}
									size={ButtonSize.S}
									title={t("list.pendingEmail.makePrimary")}
									type="button"
									onClick={() => handleAction(pendingEmail.id, "CONFIRM")}
								/>
							</div>
							<div className="w-[110px]">
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									disabled={isLoading}
									size={ButtonSize.S}
									title={t("list.pendingEmail.reject")}
									type="button"
									onClick={() => handleAction(pendingEmail.id, "REJECT")}
								/>
							</div>
						</div>
					</div>
				))}
			</div>

			{renderMessageSnackbar()}
		</div>
	);
};
